/**
 * Pricing Plan Service
 *
 * API calls for pricing plan management.
 * Public endpoint (getActivePlans) requires no auth.
 * Admin endpoints require platform_admin role.
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

export interface PricingPlan {
  id: string;
  name: string;
  internal_key: string;
  description: string | null;
  price_monthly: number | null;
  price_annual: number | null;
  max_users: number | null;
  max_projects: number | null;
  trial_duration_days: number;
  features: string;
  not_included: string;
  sort_order: number;
  is_highlighted: boolean;
  highlight_badge: string | null;
  cta_text: string;
  show_on_pricing_page: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePricingPlanInput {
  name: string;
  internal_key: string;
  description?: string;
  price_monthly?: number | null;
  price_annual?: number | null;
  max_users?: number | null;
  max_projects?: number | null;
  trial_duration_days?: number;
  features?: string;
  not_included?: string;
  sort_order?: number;
  is_highlighted?: boolean;
  highlight_badge?: string;
  cta_text?: string;
  show_on_pricing_page?: boolean;
}

export interface UpdatePricingPlanInput {
  name?: string;
  internal_key?: string;
  description?: string;
  price_monthly?: number | null;
  price_annual?: number | null;
  max_users?: number | null;
  max_projects?: number | null;
  trial_duration_days?: number;
  features?: string;
  not_included?: string;
  sort_order?: number;
  is_highlighted?: boolean;
  highlight_badge?: string | null;
  cta_text?: string;
  show_on_pricing_page?: boolean;
  is_active?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Get active pricing plans - Public (no auth)
 */
export async function getActivePlans(): Promise<PricingPlan[]> {
  const response = await axios.get<ApiResponse<PricingPlan[]>>(
    `${API_BASE_URL}/pricing-plans`
  );
  return response.data.data;
}

export interface PlanStats {
  internal_key: string;
  company_count: number;
}

/**
 * Get company counts per plan - Admin
 */
export async function getPlanStats(token: string): Promise<PlanStats[]> {
  const response = await axios.get<ApiResponse<PlanStats[]>>(
    `${API_BASE_URL}/admin/pricing-plans/stats`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get all pricing plans (including inactive) - Admin
 */
export async function getAllPlans(token: string): Promise<PricingPlan[]> {
  const response = await axios.get<ApiResponse<PricingPlan[]>>(
    `${API_BASE_URL}/admin/pricing-plans`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Get pricing plan by ID - Admin
 */
export async function getPlanById(id: string, token: string): Promise<PricingPlan> {
  const response = await axios.get<ApiResponse<PricingPlan>>(
    `${API_BASE_URL}/admin/pricing-plans/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Create pricing plan - Admin
 */
export async function createPlan(data: CreatePricingPlanInput, token: string): Promise<PricingPlan> {
  const response = await axios.post<ApiResponse<PricingPlan>>(
    `${API_BASE_URL}/admin/pricing-plans`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Update pricing plan - Admin
 */
export async function updatePlan(id: string, data: UpdatePricingPlanInput, token: string): Promise<PricingPlan> {
  const response = await axios.put<ApiResponse<PricingPlan>>(
    `${API_BASE_URL}/admin/pricing-plans/${id}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.data;
}

/**
 * Delete (deactivate) pricing plan - Admin
 */
export async function deletePlan(id: string, token: string): Promise<void> {
  await axios.delete(`${API_BASE_URL}/admin/pricing-plans/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}

export default {
  getActivePlans,
  getAllPlans,
  getPlanStats,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan
};
